import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { TabComponent, TabItemDirective, TabItemsDirective } from '@syncfusion/ej2-react-navigations';
import { GridComponent, ColumnsDirective, ColumnDirective, Inject, Sort, Filter } from '@syncfusion/ej2-react-grids';
import { useTranslation } from 'react-i18next';
import { useErrorHandler } from '../utils/errorHandler';
import { useToast } from '../hooks/useToast';
import { OpenAPI } from '../api/core/OpenAPI';
import ReleasedScheduleGrid, { type OperationRow } from './ReleasedScheduleGrid';
import { accessTokenStore } from '../auth/accessTokenStore';
import type { MaxDistanceAnalysisResult } from '../api/models/MaxDistanceAnalysisResult';
import { ProductionScheduleControllerService } from '../api/services/ProductionScheduleControllerService';
import type { CampaignData } from '../api/models/CampaignData';
import { unitLabel } from '../utils/unitLabel';

type ScheduleMode = 'optimized' | 'released' | 'active';

interface ScheduleViewContentProps {
  scheduleUuid: string;
  mode: ScheduleMode;
  isOpen: boolean;
  resourceId?: string;
  resourceTypeId?: string;
  contentHeightPx?: number;
  gridOffsetPx?: number;
  /** Schedule status - the progress column is shown only for 'completed' (released mode) */
  scheduleStatus?: string;
  /** When true: render only the operations list, without the TabComponent. Used by CurrentPlanPage. */
  operationsOnly?: boolean;
  /** Status filter for the operationsOnly path. 'all' shows everything. */
  operationStatusFilter?: 'all' | 'open' | 'running' | 'completed';
}

const ScheduleViewContent: React.FC<ScheduleViewContentProps> = ({
  scheduleUuid,
  mode,
  isOpen,
  resourceId,
  resourceTypeId,
  contentHeightPx,
  gridOffsetPx = 40,
  scheduleStatus,
  operationsOnly = false,
  operationStatusFilter = 'all'
}) => {
  const { t, i18n } = useTranslation(['common']);
  const { handleError } = useErrorHandler();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [operations, setOperations] = useState<OperationRow[]>([]);
  const [additionalResources, setAdditionalResources] = useState<any[]>([]);
  const [setupExecutions, setSetupExecutions] = useState<any[]>([]);
  const [maxDistanceAnalysis, setMaxDistanceAnalysis] = useState<MaxDistanceAnalysisResult | null>(null);
  const [unscheduledOrders, setUnscheduledOrders] = useState<any[]>([]);
  const [totalOrdersCount, setTotalOrdersCount] = useState<number | null>(null);
  const [overviewRows, setOverviewRows] = useState<Array<{ sequenceNo: number; product: string; numDoughBatches: number; numOvenBatches: number; numPieces: number }>>([]);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [intermediateRows, setIntermediateRows] = useState<Array<{ orderNumber: string; materialName: string; lotSize: number | null; baseUnit: string | null; plannedStart: string | null; plannedEnd: string | null }>>([]);
  const [intermediateLoading, setIntermediateLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const tabsRef = useRef<HTMLDivElement | null>(null);
  const TAB_HEADER_HEIGHT_PX = 48;
  const [gridHeightPx, setGridHeightPx] = useState(320);
  const lastGridHeightRef = useRef(320);

  useLayoutEffect(() => {
    if (typeof contentHeightPx === 'number' && contentHeightPx > 0) {
      const newHeight = Math.max(240, contentHeightPx - TAB_HEADER_HEIGHT_PX - gridOffsetPx);
      if (newHeight !== lastGridHeightRef.current) {
        lastGridHeightRef.current = newHeight;
        setGridHeightPx(newHeight);
      }
      return;
    }
    let rafId: number | null = null;
    const updateGridHeight = () => {
      const containerEl = containerRef.current;
      if (!containerEl) return;
      const containerHeight = containerEl.clientHeight;
      if (!containerHeight) return;
      const newHeight = Math.max(240, Math.floor(containerHeight - TAB_HEADER_HEIGHT_PX - gridOffsetPx));
      if (newHeight !== lastGridHeightRef.current) {
        lastGridHeightRef.current = newHeight;
        setGridHeightPx(newHeight);
      }
    };
    const scheduleUpdate = () => {
      if (rafId != null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = null;
        updateGridHeight();
      });
    };
    scheduleUpdate();
    window.addEventListener('resize', scheduleUpdate);
    const resizeObserver = new ResizeObserver(scheduleUpdate);
    const containerEl = containerRef.current;
    if (containerEl) resizeObserver.observe(containerEl);
    return () => {
      if (rafId != null) cancelAnimationFrame(rafId);
      window.removeEventListener('resize', scheduleUpdate);
      resizeObserver.disconnect();
    };
  }, [contentHeightPx, gridOffsetPx]);

  useEffect(() => {
    if (isOpen && scheduleUuid) {
      fetchOperations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchOperations is a local function; including it would trigger on every render
  }, [isOpen, scheduleUuid, resourceId, resourceTypeId, mode]);

  useEffect(() => {
    if (!isOpen || !scheduleUuid || (mode !== 'optimized' && mode !== 'active')) {
      setOverviewRows([]);
      setOverviewLoading(false);
      return;
    }
    let cancelled = false;
    setOverviewLoading(true);
    ProductionScheduleControllerService.getCampaigns(scheduleUuid)
      .then((campaigns: CampaignData[]) => {
        if (cancelled) return;
        const rows = (campaigns ?? [])
          .sort((a, b) => (a.sequenceNumber ?? 0) - (b.sequenceNumber ?? 0))
          .map((c: CampaignData) => ({
            sequenceNo: c.sequenceNumber ?? 0,
            product: c.materialName ?? '-',
            numDoughBatches: c.doughBatches?.length ?? 0,
            numOvenBatches: c.ovenBatches?.length ?? 0,
            numPieces: c.totalBakedQuantity ?? (c.ovenBatches ?? []).reduce((s, ob) => s + (ob.pieces ?? 0), 0)
          }));
        setOverviewRows(rows);
      })
      .catch((err) => {
        if (!cancelled) handleError(err, 'loading campaigns for overview');
      })
      .finally(() => {
        if (!cancelled) setOverviewLoading(false);
      });
    return () => { cancelled = true; };
  }, [isOpen, scheduleUuid, mode, handleError]);

  // Intermediate product tab: loads the intermediate product orders for the schedule (optimized + released).
  // Unlike the campaign tab there are no sequence numbers here - the sorting happens
  // in the backend (alphabetically by material, tie-breaker plannedStart).
  useEffect(() => {
    if (!isOpen || !scheduleUuid || (mode !== 'optimized' && mode !== 'released' && mode !== 'active')) {
      setIntermediateRows([]);
      setIntermediateLoading(false);
      return;
    }
    let cancelled = false;
    setIntermediateLoading(true);
    (async () => {
      try {
        const apiBaseUrl = OpenAPI.BASE || '';
        const url = `${apiBaseUrl}/api/scheduling/intermediate-orders?scheduleUuid=${encodeURIComponent(scheduleUuid)}`;
        const response = await fetch(url, { method: 'GET', headers: await getAuthHeaders() });
        if (!response.ok) throw new Error(`Failed to fetch intermediate orders (${response.status})`);
        const data = await response.json();
        if (cancelled) return;
        setIntermediateRows(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) handleError(err, 'loading intermediate orders');
      } finally {
        if (!cancelled) setIntermediateLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // getAuthHeaders is stable per-render in this file; no need to track it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, scheduleUuid, mode]);

  const getAuthHeaders = async () => {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    if (i18n.language) {
      headers['Accept-Language'] = i18n.language;
    }
    const token = accessTokenStore.get();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const storedTenant = localStorage.getItem('selectedTenant');
    if (storedTenant) {
      const tenant = JSON.parse(storedTenant);
      headers['X-Tenant-ID'] = tenant.uuid;
    }
    return headers;
  };

  const fetchOperations = async () => {
    setLoading(true);
    try {
      const apiBaseUrl = OpenAPI.BASE || '';
      const params = new URLSearchParams();
      params.append('scheduleUuid', scheduleUuid);
      if (resourceId) {
        params.append('resourceId', resourceId);
      }
      if (resourceTypeId) {
        params.append('resourceTypeId', resourceTypeId);
      }

      const endpoint = mode === 'optimized' ? 'optimized-schedule' : 'released-schedule';
      const url = `${apiBaseUrl}/api/scheduling/${endpoint}?${params.toString()}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: await getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch ${mode} schedule data`);
      }

      const data = await response.json();
      const transformedOperations: OperationRow[] = [];

      if (data.resources) {
        data.resources.forEach((resource: any) => {
          if (resource.operations && resource.operations.length > 0) {
            resource.operations.forEach((op: any) => {
              transformedOperations.push({
                resourceName: resource.resourceName || '',
                resourceTypeName: resource.resourceTypeName || '',
                operationName: op.operationName || '',
                productName: op.productName || op.orderName || '',
                campaignId: op.campaignId || '',
                plannedStart: op.plannedStart || '',
                plannedEnd: op.plannedEnd || '',
                targetStart: op.targetStart || '',
                targetEnd: op.targetEnd || '',
                actualStart: op.actualStart ?? null,
                actualEnd: op.actualEnd ?? null,
                executionStatus: op.executionStatus || '',
                completionPercentage: op.completionPercentage ?? null,
                duration: op.duration || 0
              });
            });
          }
        });
      }

      transformedOperations.sort((a, b) => {
        const timeA = new Date(a.targetStart).getTime();
        const timeB = new Date(b.targetStart).getTime();
        if (timeA !== timeB) {
          return timeA - timeB;
        }
        return (a.resourceName || '').localeCompare(b.resourceName || '');
      });

      setOperations(transformedOperations);

      if (mode === 'optimized' && data.additionalResources) {
        const sorted = [...data.additionalResources].sort((a: any, b: any) => {
          const timeA = new Date(a.startTime || 0).getTime();
          const timeB = new Date(b.startTime || 0).getTime();
          return timeA - timeB;
        });
        setAdditionalResources(sorted);
      } else {
        setAdditionalResources([]);
      }

      if (mode === 'optimized' && data.setupExecutions) {
        setSetupExecutions(Array.isArray(data.setupExecutions) ? data.setupExecutions : []);
      } else {
        setSetupExecutions([]);
      }

      if (mode === 'optimized' && data.maxDistanceAnalysis) {
        setMaxDistanceAnalysis(data.maxDistanceAnalysis as MaxDistanceAnalysisResult);
      } else {
        setMaxDistanceAnalysis(null);
      }

      if (mode === 'optimized' && data.unscheduledOrders) {
        setUnscheduledOrders(Array.isArray(data.unscheduledOrders) ? data.unscheduledOrders : []);
      } else {
        setUnscheduledOrders([]);
      }

      if (mode === 'optimized' && data.totalOrdersCount != null) {
        setTotalOrdersCount(data.totalOrdersCount as number);
      } else {
        setTotalOrdersCount(null);
      }
    } catch (error) {
      handleError(error, `loading ${mode} schedule data`);
      showToast('error', t('common.error'), `Failed to load ${mode} schedule data`);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (i18n.language === 'de') {
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const seconds = date.getSeconds().toString().padStart(2, '0');
        return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
      }
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).format(date);
    } catch {
      return dateString;
    }
  };

  const formatDuration = (hours: number) => {
    const totalSeconds = Math.round((hours ?? 0) * 3600);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return [h, m, s].map((v) => v.toString().padStart(2, '0')).join(':');
  };

  /** Format setup duration from start/end so display always matches the start/end time columns (no rounding). */
  const formatSetupDuration = (startTime: string | undefined, endTime: string | undefined, fallbackDurationHours?: number) => {
    if (startTime && endTime) {
      try {
        const start = new Date(startTime).getTime();
        const end = new Date(endTime).getTime();
        const totalSeconds = Math.round((end - start) / 1000);
        if (totalSeconds < 0) return formatDuration(fallbackDurationHours ?? 0);
        const h = Math.floor(totalSeconds / 3600);
        const m = Math.floor((totalSeconds % 3600) / 60);
        const s = totalSeconds % 60;
        return [h, m, s].map((v) => v.toString().padStart(2, '0')).join(':');
      } catch {
        // fall through to fallback
      }
    }
    return formatDuration(fallbackDurationHours ?? 0);
  };

  return (
    <div
      ref={containerRef}
      className="h-full flex flex-col min-h-0"
      style={{ minHeight: 0, height: typeof contentHeightPx === 'number' ? `${contentHeightPx}px` : undefined }}
    >
      <div ref={tabsRef} style={{ flex: 1, minHeight: 0 }}>
        {operationsOnly ? (
          <div style={{ height: '100%', minHeight: 0 }}>
            <ReleasedScheduleGrid
              operations={operations.filter((op) => {
                switch (operationStatusFilter) {
                  case 'open':
                    return op.executionStatus !== 'COMPLETED' && op.executionStatus !== 'CANCELLED';
                  case 'running':
                    return op.executionStatus === 'IN_PROGRESS';
                  case 'completed':
                    return op.executionStatus === 'COMPLETED';
                  default:
                    return true;
                }
              })}
              loading={loading}
              height={gridHeightPx}
              mode={mode}
              showProgressColumn={mode === 'active' || (mode === 'released' && scheduleStatus === 'completed')}
            />
          </div>
        ) : (<>
        {mode === 'optimized' && unscheduledOrders.length > 0 && (
          <div className="bg-red-50 border border-red-200 text-red-800 text-sm px-4 py-2 rounded mb-2 flex items-center gap-2">
            <span className="font-bold">&#x26A0;</span>
            <span>
              {t('productionSchedule.optimizedScheduleView.unscheduledOrders.banner', {
                count: unscheduledOrders.length,
                total: totalOrdersCount ?? (unscheduledOrders.length + overviewRows.length),
                defaultValue: '{{count}} von {{total}} Aufträgen konnten nicht eingeplant werden — Details im Tab „Nicht eingeplant”.',
              })}
            </span>
          </div>
        )}
        <TabComponent>
          <TabItemsDirective>
            {(mode === 'optimized' || mode === 'active') && (
              <TabItemDirective
                header={{ text: t('productionSchedule.optimizedScheduleView.tabs.campaigns', 'Kampagnen') }}
                content={() => (
                  <div style={{ height: '100%', minHeight: 0 }}>
                    {overviewLoading ? (
                      <div className="flex items-center justify-center h-64">
                        <div className="text-lg">{t('common.loading')}</div>
                      </div>
                    ) : (
                      <GridComponent
                        locale="de-DE"
                        dataSource={overviewRows}
                        allowPaging={false}
                        allowSorting={true}
                        allowFiltering={true}
                        height={gridHeightPx}
                        width="100%"
                      >
                        <ColumnsDirective>
                          <ColumnDirective
                            field="sequenceNo"
                            headerText={t('productionSchedule.optimizedScheduleView.overview.sequenceNo', 'Sequenz')}
                            width="100"
                            type="number"
                          />
                          <ColumnDirective
                            field="product"
                            headerText={t('productionSchedule.optimizedScheduleView.overview.product', 'Produkt')}
                            width="200"
                          />
                          <ColumnDirective
                            field="numDoughBatches"
                            headerText={t('productionSchedule.optimizedScheduleView.overview.numDoughBatches', 'Anz. Teig-Chargen')}
                            width="140"
                            type="number"
                          />
                          <ColumnDirective
                            field="numOvenBatches"
                            headerText={t('productionSchedule.optimizedScheduleView.overview.numOvenBatches', 'Anz. Ofen-Chargen')}
                            width="150"
                            type="number"
                          />
                          <ColumnDirective
                            field="numPieces"
                            headerText={t('productionSchedule.optimizedScheduleView.overview.numPieces', 'Stückzahl')}
                            width="120"
                            type="number"
                          />
                        </ColumnsDirective>
                        <Inject services={[Sort, Filter]} />
                      </GridComponent>
                    )}
                  </div>
                )}
              />
            )}
            {(mode === 'optimized' || mode === 'released' || mode === 'active') && intermediateRows.length > 0 && (
              <TabItemDirective
                header={{ text: t('productionSchedule.optimizedScheduleView.tabs.intermediates', 'Vorprodukte') }}
                content={() => (
                  <div style={{ height: '100%', minHeight: 0 }}>
                    {intermediateLoading ? (
                      <div className="flex items-center justify-center h-64">
                        <div className="text-lg">{t('common.loading')}</div>
                      </div>
                    ) : (
                      <GridComponent
                        locale="de-DE"
                        dataSource={intermediateRows.map((r) => ({
                          orderNumber: r.orderNumber ?? '',
                          materialName: r.materialName ?? '',
                          lotSizeDisplay: r.lotSize != null
                            ? `${r.lotSize}${r.baseUnit ? ' ' + unitLabel(r.baseUnit) : ''}`
                            : '',
                          plannedStart: r.plannedStart ? formatDate(r.plannedStart) : '',
                          plannedEnd: r.plannedEnd ? formatDate(r.plannedEnd) : '',
                        }))}
                        allowPaging={false}
                        allowSorting={true}
                        allowFiltering={true}
                        height={gridHeightPx}
                        width="100%"
                      >
                        <ColumnsDirective>
                          <ColumnDirective
                            field="materialName"
                            headerText={t('productionSchedule.optimizedScheduleView.intermediates.material', 'Material')}
                            width="200"
                          />
                          <ColumnDirective
                            field="lotSizeDisplay"
                            headerText={t('productionSchedule.optimizedScheduleView.intermediates.lotSize', 'Losgröße')}
                            width="140"
                            textAlign="Right"
                          />
                          <ColumnDirective
                            field="plannedStart"
                            headerText={t('productionSchedule.optimizedScheduleView.intermediates.plannedStart', 'Geplanter Start')}
                            width="180"
                          />
                          <ColumnDirective
                            field="plannedEnd"
                            headerText={t('productionSchedule.optimizedScheduleView.intermediates.plannedEnd', 'Geplantes Ende')}
                            width="180"
                          />
                        </ColumnsDirective>
                        <Inject services={[Sort, Filter]} />
                      </GridComponent>
                    )}
                  </div>
                )}
              />
            )}
            <TabItemDirective
              header={{ text: t('productionSchedule.optimizedScheduleView.tabs.primaryResources', 'Vorgänge') }}
              content={() => (
                <div style={{ height: '100%', minHeight: 0 }}>
                  <ReleasedScheduleGrid
                    operations={operations}
                    loading={loading}
                    height={gridHeightPx}
                    mode={mode}
                    showProgressColumn={mode === 'active' || (mode === 'released' && scheduleStatus === 'completed')}
                  />
                </div>
              )}
            />
            {mode === 'optimized' && (
              <TabItemDirective
                header={{ text: t('productionSchedule.optimizedScheduleView.tabs.setupTimes', 'Rüstzeiten') }}
                content={() => (
                  <div style={{ height: '100%', minHeight: 0 }}>
                    {loading ? (
                      <div className="flex items-center justify-center h-64">
                        <div className="text-lg">{t('common.loading')}</div>
                      </div>
                    ) : (
                      <GridComponent
                        locale="de-DE"
                        dataSource={setupExecutions}
                        allowPaging={false}
                        allowSorting={true}
                        allowFiltering={true}
                        height={gridHeightPx}
                        width="100%"
                      >
                        <ColumnsDirective>
                          <ColumnDirective
                            field="resourceName"
                            headerText={t('productionSchedule.optimizedScheduleView.setupTimes.resource', 'Ressource')}
                            width="200"
                          />
                          <ColumnDirective
                            field="resourceTypeName"
                            headerText={t('productionSchedule.optimizedScheduleView.setupTimes.resourceType', 'Ressourcentyp')}
                            width="150"
                          />
                          <ColumnDirective
                            field="fromProduct"
                            headerText={t('productionSchedule.optimizedScheduleView.setupTimes.fromProduct', 'Von Produkt')}
                            width="180"
                          />
                          <ColumnDirective
                            field="toProduct"
                            headerText={t('productionSchedule.optimizedScheduleView.setupTimes.toProduct', 'Zu Produkt')}
                            width="180"
                          />
                          <ColumnDirective
                            field="startTime"
                            headerText={t('productionSchedule.optimizedScheduleView.setupTimes.startTime', 'Startzeit')}
                            width="150"
                            template={(props: any) => formatDate(props.startTime)}
                          />
                          <ColumnDirective
                            field="endTime"
                            headerText={t('productionSchedule.optimizedScheduleView.setupTimes.endTime', 'Endzeit')}
                            width="150"
                            template={(props: any) => formatDate(props.endTime)}
                          />
                          <ColumnDirective
                            field="duration"
                            headerText={t('productionSchedule.optimizedScheduleView.setupTimes.duration', 'Dauer')}
                            width="120"
                            template={(props: any) => formatSetupDuration(props.startTime, props.endTime, props.duration)}
                          />
                        </ColumnsDirective>
                        <Inject services={[Sort, Filter]} />
                      </GridComponent>
                    )}
                  </div>
                )}
              />
            )}
            {mode === 'optimized' && maxDistanceAnalysis && (
              <TabItemDirective
                header={{ text: t('productionSchedule.optimizedScheduleView.tabs.maxDistanceAnalysis', 'MaxDistance-Analyse') }}
                content={() => (
                  <div style={{ height: '100%', minHeight: 0 }} className="p-4">
                    {maxDistanceAnalysis.report && (
                      <div className="mb-4">
                        <div className="font-semibold mb-2">
                          {t('productionSchedule.optimizedScheduleView.maxDistanceAnalysis.summary', 'Zusammenfassung')}
                        </div>
                        <pre className="whitespace-pre-wrap text-sm bg-gray-100 dark:bg-gray-800 p-3 rounded overflow-auto max-h-48">
                          {maxDistanceAnalysis.report}
                        </pre>
                      </div>
                    )}
                    {(maxDistanceAnalysis.totalConstraints != null || maxDistanceAnalysis.disabledConstraints != null) && !maxDistanceAnalysis.report && (
                      <div className="mb-4 text-sm">
                        {maxDistanceAnalysis.totalConstraints != null && (
                          <span>{maxDistanceAnalysis.enabledConstraints ?? '-'} / {maxDistanceAnalysis.totalConstraints} Constraints aktiv, {maxDistanceAnalysis.disabledConstraints ?? 0} relaxiert.</span>
                        )}
                      </div>
                    )}
                    {maxDistanceAnalysis.disabledConstraintDetails && maxDistanceAnalysis.disabledConstraintDetails.length > 0 ? (
                      <GridComponent
                        locale="de-DE"
                        dataSource={maxDistanceAnalysis.disabledConstraintDetails}
                        allowPaging={false}
                        allowSorting={true}
                        allowFiltering={true}
                        height={Math.max(200, gridHeightPx - 120)}
                        width="100%"
                      >
                        <ColumnsDirective>
                          <ColumnDirective
                            field="orderName"
                            headerText={t('productionSchedule.optimizedScheduleView.maxDistanceAnalysis.orderName', 'Auftrag')}
                            width="180"
                          />
                          <ColumnDirective
                            field="fromOperationName"
                            headerText={t('productionSchedule.optimizedScheduleView.maxDistanceAnalysis.fromOperation', 'Von Vorgang')}
                            width="150"
                          />
                          <ColumnDirective
                            field="toOperationName"
                            headerText={t('productionSchedule.optimizedScheduleView.maxDistanceAnalysis.toOperation', 'Zu Vorgang')}
                            width="150"
                          />
                          <ColumnDirective
                            field="maxDistanceValue"
                            headerText={t('productionSchedule.optimizedScheduleView.maxDistanceAnalysis.maxDistanceSeconds', 'Max. Abstand (s)')}
                            width="120"
                            type="number"
                          />
                          <ColumnDirective
                            field="actualGapSeconds"
                            headerText={t('productionSchedule.optimizedScheduleView.maxDistanceAnalysis.actualGapSeconds', 'Tatsächlicher Abstand (s)')}
                            width="160"
                            type="number"
                          />
                          <ColumnDirective
                            field="excessSeconds"
                            headerText={t('productionSchedule.optimizedScheduleView.maxDistanceAnalysis.excessSeconds', 'Überschreitung (s)')}
                            width="120"
                            type="number"
                          />
                          <ColumnDirective
                            field="reason"
                            headerText={t('productionSchedule.optimizedScheduleView.maxDistanceAnalysis.reason', 'Grund')}
                            width="200"
                          />
                        </ColumnsDirective>
                        <Inject services={[Sort, Filter]} />
                      </GridComponent>
                    ) : (
                      <div className="text-gray-500 dark:text-gray-400">
                        {t('productionSchedule.optimizedScheduleView.maxDistanceAnalysis.noData', 'Keine MaxDistance-Analyse verfügbar.')}
                      </div>
                    )}
                  </div>
                )}
              />
            )}
            {mode === 'optimized' && unscheduledOrders.length > 0 && (
              <TabItemDirective
                header={{ text: t('productionSchedule.optimizedScheduleView.tabs.unscheduledOrders', 'Nicht eingeplant') + ` (${unscheduledOrders.length})` }}
                content={() => (
                  <div style={{ height: '100%', minHeight: 0 }} className="p-4">
                    <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3 mb-4">
                      {t('productionSchedule.optimizedScheduleView.unscheduledOrders.description',
                        'Die folgenden Aufträge konnten nicht eingeplant werden. Die Analyse zeigt mögliche Ursachen und Lösungsvorschläge.')}
                    </p>
                    {unscheduledOrders.map((uo: any, idx: number) => (
                      <div key={idx} className="mb-4 border border-gray-200 rounded-lg p-4 bg-white">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-red-600 font-bold text-lg">&#x2716;</span>
                          <span className="font-semibold text-base">{uo.orderName || uo.orderId}</span>
                          {uo.explanation?.bottleneckType && (
                            <span className="text-xs font-medium bg-red-100 text-red-800 border border-red-200 rounded px-2 py-0.5">
                              {t(`productionSchedule.optimizedScheduleView.unscheduledOrders.bottleneckTypes.${uo.explanation.bottleneckType}`, { defaultValue: uo.explanation.bottleneckType })}
                            </span>
                          )}
                        </div>
                        {uo.reason && uo.reason !== 'OTHER' && (
                          <div className="text-sm text-gray-500 mb-2">
                            {t(`productionSchedule.optimizedScheduleView.unscheduledOrders.reasons.${uo.reason}`, { defaultValue: uo.reason })}
                          </div>
                        )}
                        {uo.explanation && (
                          <div className="mt-2">
                            {uo.explanation.summary && (
                              <div className="text-sm text-gray-800 mb-2">
                                {uo.explanation.summary}
                              </div>
                            )}
                            {uo.explanation.suggestions && uo.explanation.suggestions.length > 0 && (
                              <div className="mt-2">
                                <div className="text-xs font-semibold text-gray-600 mb-1">
                                  {t('productionSchedule.optimizedScheduleView.unscheduledOrders.suggestions', 'Lösungsvorschläge:')}
                                </div>
                                <ul className="list-none space-y-1">
                                  {uo.explanation.suggestions.map((s: any, si: number) => (
                                    <li key={si} className="text-sm text-green-800 bg-green-50 border border-green-200 rounded px-3 py-1.5">
                                      <span className="mr-1">&#x1F4A1;</span>
                                      {s.type && (
                                        <span className="text-xs font-medium bg-green-100 text-green-900 border border-green-300 rounded px-1.5 py-0.5 mr-2">
                                          {t(`productionSchedule.optimizedScheduleView.unscheduledOrders.suggestionTypes.${s.type}`, { defaultValue: s.type })}
                                        </span>
                                      )}
                                      {s.description}
                                      {s.currentValue && s.suggestedValue && (
                                        <span className="text-xs text-gray-500 ml-2">
                                          ({t('productionSchedule.optimizedScheduleView.unscheduledOrders.current', 'aktuell')}: {s.currentValue}, {t('productionSchedule.optimizedScheduleView.unscheduledOrders.recommended', 'empfohlen')}: {s.suggestedValue})
                                        </span>
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {uo.explanation.detail && (
                              <details className="mt-2">
                                <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600">
                                  {t('productionSchedule.optimizedScheduleView.unscheduledOrders.technicalDetails', 'Technische Details')}
                                </summary>
                                <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-2 mt-1 whitespace-pre-wrap overflow-auto max-h-40">
                                  {uo.explanation.detail}
                                </pre>
                              </details>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              />
            )}
            <TabItemDirective
              header={{ text: t('productionSchedule.optimizedScheduleView.tabs.secondaryResources', 'Sekundär-Ressourcen') }}
              content={() => (
                <div style={{ height: '100%', minHeight: 0 }}>
                  {loading ? (
                    <div className="flex items-center justify-center h-64">
                      <div className="text-lg">{t('common.loading')}</div>
                    </div>
                  ) : (
                    <GridComponent
                      locale="de-DE"
                      dataSource={additionalResources}
                      allowPaging={false}
                      allowSorting={true}
                      allowFiltering={true}
                      height={gridHeightPx}
                      width="100%"
                    >
                      <ColumnsDirective>
                        <ColumnDirective
                          field="resourceName"
                          headerText={t('productionSchedule.optimizedScheduleView.secondaryResources.resourceName', 'Ressource')}
                          width="200"
                        />
                        <ColumnDirective
                          field="resourceTypeName"
                          headerText={t('productionSchedule.optimizedScheduleView.secondaryResources.resourceTypeName', 'Ressourcentyp')}
                          width="150"
                        />
                        <ColumnDirective
                          field="productName"
                          headerText={t('productionSchedule.optimizedScheduleView.secondaryResources.productName', 'Produkt')}
                          width="200"
                        />
                        <ColumnDirective
                          field="fromRelation"
                          headerText={t('form.fromRelation', 'Von Beziehung')}
                          width="100"
                          template={(props: any) => (
                            <span>{props.fromRelation ? String(t(`form.relationType.${props.fromRelation}`, props.fromRelation)) : '-'}</span>
                          )}
                        />
                        <ColumnDirective
                          field="fromOperationName"
                          headerText={t('form.fromOperation', 'Von Vorgang')}
                          width="140"
                        />
                        <ColumnDirective
                          field="toRelation"
                          headerText={t('form.toRelation', 'Zu Beziehung')}
                          width="100"
                          template={(props: any) => (
                            <span>{props.toRelation ? String(t(`form.relationType.${props.toRelation}`, props.toRelation)) : '-'}</span>
                          )}
                        />
                        <ColumnDirective
                          field="toOperationName"
                          headerText={t('form.toOperation', 'Zu Vorgang')}
                          width="140"
                        />
                        <ColumnDirective
                          field="quantity"
                          headerText={t('productionSchedule.optimizedScheduleView.secondaryResources.quantity', 'Menge')}
                          width="100"
                          type="number"
                          format="N2"
                        />
                        <ColumnDirective
                          field="startTime"
                          headerText={t('productionSchedule.optimizedScheduleView.secondaryResources.startTime', 'Startzeit')}
                          width="150"
                          template={(props: any) => formatDate(props.startTime)}
                        />
                        <ColumnDirective
                          field="endTime"
                          headerText={t('productionSchedule.optimizedScheduleView.secondaryResources.endTime', 'Endzeit')}
                          width="150"
                          template={(props: any) => formatDate(props.endTime)}
                        />
                        <ColumnDirective
                          field="duration"
                          headerText={t('productionSchedule.optimizedScheduleView.secondaryResources.duration', 'Dauer')}
                          width="120"
                          template={(props: any) => formatDuration(props.duration)}
                        />
                      </ColumnsDirective>
                      <Inject services={[Sort, Filter]} />
                    </GridComponent>
                  )}
                </div>
              )}
            />
          </TabItemsDirective>
        </TabComponent>
        </>)}
      </div>
    </div>
  );
};

export default ScheduleViewContent;
